import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { highlightedTranslations } from '../../../translations/highlightedTranslations';
import { Button, Modal } from '../../../stories/molecules';
import { WidgetType } from '../../home/types';
import { AreaWidgetType } from '../../areaOverview/util';
import { Icon, Icons, Typography } from '../../../stories/atoms';
import { commonTranslations } from '../../../translations';
import { useStoreInformation } from '../../../hooks';
import { muiTheme } from '../../../theme';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Translation } from '../../../utils';
import {
  StyledTitleTypography,
  StyledIcon,
  StyledNoteTypography,
  StyledColumnFlexContainer,
} from './HighlightModal.styles';

export type HighlightedModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  hasResponse: boolean;
  hasError: boolean;
  hasLoading: boolean;
  widgetType: WidgetType | AreaWidgetType;
  isWidgetHighlighted: boolean;
  translationData: Translation;
};

export const HighlightedWidgetModal: FC<HighlightedModalProps> = observer(
  ({
    isOpen,
    onClose,
    onSubmit,
    hasResponse,
    hasError,
    hasLoading,
    widgetType,
    isWidgetHighlighted,
    translationData,
  }) => {
    const { t } = useTranslation();
    const { close, tryAgain } = commonTranslations;
    const { areaName } = useStoreInformation();

    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

    const renderHighlightedText = (translationKey: string, defaultValue: string) => {
      const text = t(translationKey, defaultValue);
      const parts = text.split('{{widgetName}}');

      return (
        <>
          {parts[0]}
          <strong color="primary">{translationData.defaultValue}</strong>
          {parts[1]}
        </>
      );
    };

    return (
      <>
        <Modal
          isOpen={isOpen}
          handleClose={onClose}
          ariaDescribedby="highlight-widget-modal"
          data-testid="highlighted-widget-modal"
        >
          {!hasError && !hasResponse && (
            <ColumnFlexContainer gap={[3]} padding={isMobile ? [8, 4] : [8]} alignItems="center">
              <RowFlexContainer alignItems="center" justifyContent="center" gap={[4]}>
                <StyledIcon
                  icon={hasLoading ? Icons.SPINNER : isWidgetHighlighted ? Icons.STAR : Icons.STAR_FILLED}
                  size="medium"
                  data-testid="highlight-modal-icon"
                />
                <StyledTitleTypography data-testid="highlight-modal-title" type="h3" textAlign="left">
                  {isWidgetHighlighted
                    ? t(
                        highlightedTranslations.title.unhighlight.key,
                        highlightedTranslations.title.unhighlight.defaultValue,
                      )
                    : t(
                        highlightedTranslations.title.highlight.key,
                        highlightedTranslations.title.highlight.defaultValue,
                      )}
                </StyledTitleTypography>
              </RowFlexContainer>
              <StyledNoteTypography
                type="body3"
                textAlign="center"
                padding={[2]}
                data-testid="highlight-modal-note"
              >
                {isWidgetHighlighted
                  ? t(
                      highlightedTranslations.note.unhighlight.key,
                      highlightedTranslations.note.unhighlight.defaultValue,
                      { areaName },
                    )
                  : t(
                      highlightedTranslations.note.highlight.key,
                      highlightedTranslations.note.highlight.defaultValue,
                      { areaName },
                    )}
              </StyledNoteTypography>
              <Typography textAlign="center" type="body2" data-testid="highlight-modal-comment">
                {isWidgetHighlighted
                  ? renderHighlightedText(
                      highlightedTranslations.comment.unhighlight.key,
                      highlightedTranslations.comment.unhighlight.defaultValue,
                    )
                  : renderHighlightedText(
                      highlightedTranslations.comment.highlight.key,
                      highlightedTranslations.comment.highlight.defaultValue,
                    )}
              </Typography>

              <RowFlexContainer gap={[4]} width="100%" alignItems="center">
                <Button
                  onClick={onClose}
                  label={t(commonTranslations.cancel.key, commonTranslations.cancel.defaultValue)}
                  variant="secondary"
                  fullWidth
                  data-testid="highlight-modal-cancel-button"
                />
                <Button
                  label={t(commonTranslations.confirm.key, commonTranslations.confirm.defaultValue)}
                  data-testid="highlight-modal-confirm-button"
                  fullWidth
                  onClick={onSubmit}
                />
              </RowFlexContainer>
            </ColumnFlexContainer>
          )}

          {hasResponse && (
            <StyledColumnFlexContainer
              alignItems="center"
              justifyContent="center"
              gap={[3]}
              padding={[8]}
              isMobileView={isMobile}
              data-testid="highlight-modal-success"
            >
              <Icon
                icon={Icons.VALIDACTION}
                size="x-large"
                color="validationGreen"
                data-testid="highlight-modal-success-icon"
              />
              <Typography textAlign="center" data-testid="highlight-modal-success-text">
                {isWidgetHighlighted
                  ? t(highlightedTranslations.successtext.highlight.key, {
                      defaultValue: highlightedTranslations.successtext.highlight.defaultValue,
                    })
                  : t(highlightedTranslations.successtext.unhighlight.key, {
                      defaultValue: highlightedTranslations.successtext.unhighlight.defaultValue,
                    })}
              </Typography>

              <Button
                label={t(close.key, close.defaultValue)}
                fullWidth
                onClick={onClose}
                data-testid="highlight-modal-success-close-button"
              />
            </StyledColumnFlexContainer>
          )}
          {hasError && (
            <StyledColumnFlexContainer
              alignItems="center"
              justifyContent="center"
              gap={[3]}
              padding={[8]}
              isMobileView={isMobile}
              data-testid="highlight-modal-error"
            >
              <Icon icon={Icons.REFRESH} size="x-large" color="red" data-testid="highlight-modal-error-icon" />
              <Typography textAlign="center" data-testid="highlight-modal-error-text">
                {t(highlightedTranslations.errortext.key, {
                  defaultValue: highlightedTranslations.errortext.defaultValue,
                  widgetType,
                })}
              </Typography>
              <RowFlexContainer gap={[4]} width="100%" alignItems="center" justifyContent="center">
                <Button
                  label={t(tryAgain.key, tryAgain.defaultValue)}
                  fullWidth
                  onClick={onSubmit}
                  data-testid="highlight-modal-try-again-button"
                />
                <Button
                  label={t(close.key, close.defaultValue)}
                  fullWidth
                  onClick={onClose}
                  data-testid="highlight-modal-error-close-button"
                />
              </RowFlexContainer>
            </StyledColumnFlexContainer>
          )}
        </Modal>
      </>
    );
  },
);
